/* ============================================
   EMAIL TEMPLATES — REUSABLE COPY BLOCKS
   Asset: email-tpl-blocks.js
   CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-19)

   The sentences more than one email needs. email-tpl-core.js knows how to READ a deal;
   this file knows how to SAY things about one. A template file should mostly be a list of
   blocks in an order, with only the sentences unique to that email written inline.

   Ryan: "reusable components/snippets/insertions/logic is key ... set us up for long term
   success." So the test for putting something here is: would a SECOND email want this
   sentence? If not, it belongs in the template that says it.

   EVERY BLOCK RETURNS RUNS OR null. null means "this email has nothing to say here" and
   the caller simply does not push a paragraph - which is how an email stays short when
   there is no call to reference, no product to name, no voicemail to count.

   ⛔ NO EM DASHES IN COPY WE WRITE. Every string in this file is customer-facing, and
   every one of them is ours rather than Ryan's, so the rule is absolute here. ⚠️ A family
   file rendering copy he wrote reproduces his punctuation verbatim - see email-tpl-core.js.
   ============================================ */
(function () {
  'use strict';

  var T = window.RQDTpl;
  if (!T) { console.error('[email-tpl] blocks loaded before the kernel'); return; }

  /* ============================================
     THE COPY EVERY EMAIL SHARES
     CUSTOM_HUMANCOPYEDIT_BY_CLAUDE (Ryan 2026-09-03)
     ============================================
     Every sentence below is editable from Abby Emails -> Templates & snippets without a deploy.
     What is NOT here is the part that decides WHICH of them runs - that stays in the functions
     underneath, because "did they leave a voicemail" and "how many products are on this deal"
     are facts about a deal, not wording.

     ⚠️ EDIT A GROUP TOGETHER. A dot in the key groups variants, and the editor shows a group
     as a set: `greeting.named` and `greeting.unnamed` are the same greeting with and without a
     name, and changing one without the other is how an email quietly changes voice depending on
     whether the customer's name happened to be on the deal.

     ⛔ THESE ARE SHARED, so an edit here lands on EVERY email that uses the block - which is
     usually the point (house voice moves at once) and is occasionally a surprise. The tab says
     which emails a shared sentence reaches. */
  var COPY = T.definePhrases('blocks', {
    'greeting.named':   'Hi {FIRST},',
    'greeting.unnamed': 'Hi there,',

    /* `warm` is the first-quote flavour; the follow-ups use the quieter full stop. Both exist
       because Ryan wrote both, which is why they are two groups and not one. */
    'signOff.warm.named':    'Thanks again, {FIRST}!',
    'signOff.warm.unnamed':  'Thanks again!',
    'signOff.plain.named':   'Thanks, {FIRST}.',
    'signOff.plain.unnamed': 'Thanks!',

    /* THE sentence that makes a follow-up feel written rather than generated. Four outcomes,
       each with a "just now" variant, because "just" moves in front of the verb and no amount
       of token juggling puts it there. */
    'call.text.just':        'I just sent you a text{TAIL}.',
    'call.text.dated':       'I sent you a text{WHEN}{TAIL}.',
    'call.voicemail.just':   'I just called and left you a voicemail{TAIL}.',
    'call.voicemail.dated':  'I called and left you a voicemail{WHEN}{TAIL}.',
    'call.tried.just':       'I just tried you by phone{TAIL}.',
    'call.tried.dated':      'I tried you by phone{WHEN}{TAIL}.',
    /* Thanking someone for their time is the only safe thing to say about a call we know they
       answered, so this one has no "just" variant - it reads fine either way. */
    'call.connected':        'Thanks again for your time on the phone{WHEN}.',

    /* The tally, for the email that is explicitly counting attempts. {PARTS} is the clauses
       below, joined into "A, B, and C" - so this sentence owns the lead-in and the joining is
       grammar.
       ⛔ NO DOTS ON THE FIRST THREE, AND THAT IS DELIBERATE. A dot groups keys as VARIANTS, and
       the editor tells you to change a group together because exactly one of them runs. These
       three are not alternatives - they COMBINE, and a customer can read all three in one
       sentence. Only the email count is a real one-of, so only it keeps the dot. */
    'tallyLead':             "I've {PARTS}",
    'tallyVoicemails':       'left you {COUNT}',
    'tallyTexts':            'sent you {COUNT}',
    'tallyEmails.once':      'emailed you once',
    'tallyEmails.couple':    'emailed you a couple of times',
    'tallyEmails.few':       'emailed you a few times',
  }, {
    FIRST: "the customer's first name",
    /* ⛔ THE TWO THAT CARRY THEIR OWN LEADING SPACE, and the reason the sentences above look
       like they are missing one. Both can be EMPTY, so a space written into the sentence would
       land in front of the full stop on every send that has no date and no reason. */
    WHEN:  'when it happened, e.g. " last week". Carries its own leading space and is often '
           + 'empty, so do not put a space in front of it.',
    TAIL:  'why we called, e.g. " to follow up on the quote". Carries its own leading space and '
           + 'is often empty, so do not put a space in front of it.',
    PARTS: 'the clauses below, joined: "left you a couple of voicemails, sent you a text, and '
           + 'emailed you a few times"',
    COUNT: 'a counted noun phrase: "a voicemail", "a couple of texts", "a few voicemails"',
  });

  /* ── counting things a customer will read as English ──────────────────────
     1 → "a voicemail", 2 → "a couple of voicemails", 3+ → "a few voicemails".
     ⚠️ Ryan named "a couple of voicemails" specifically, so 2 is its own rung rather than
     rounding up into "a few" - "a couple" is exact and a customer notices when it is not. */
  function quantity(n, singular, plural) {
    var p = plural || (singular + 's');
    if (n <= 0) return '';
    if (n === 1) return 'a ' + singular;
    if (n === 2) return 'a couple of ' + p;
    return 'a few ' + p;
  }

  /* "A, B, and C" from plain strings, for the cases that carry no links. */
  function joinWords(list) {
    var a = (list || []).filter(Boolean);
    if (!a.length) return '';
    if (a.length === 1) return a[0];
    if (a.length === 2) return a[0] + ' and ' + a[1];
    return a.slice(0, -1).join(', ') + ', and ' + a[a.length - 1];
  }

  /* ── greeting + sign-off ──────────────────────────────────────────────────
     One definition so a rep never meets two different greetings from two templates. */
  /* The words are in COPY at the top of this file; what is left here is the CHOICE between
     them, which is a fact about the deal (do we know their name?) rather than wording. */
  function greeting(first) {
    return first ? COPY['greeting.named']({ FIRST: first }) : COPY['greeting.unnamed']({});
  }

  /* `warm` is the first-quote flavour ("Thanks again, Mike!"); the follow-ups use the
     quieter full stop. Both exist because Ryan wrote both, which is why they are two groups
     in COPY rather than one. */
  function signOff(first, warm) {
    var k = 'signOff.' + (warm ? 'warm' : 'plain') + '.' + (first ? 'named' : 'unnamed');
    return COPY[k](first ? { FIRST: first } : {});
  }

  /* ── who we are writing to ────────────────────────────────────────────────
     Draft-order customer, then the dashboard row, then the To address. q.customer on the
     dashboard row is the same person under a different key; the To address is a last
     resort and only yields a name when it looks like one. */
  function customerFirstName(details, dq, ctx) {
    var quote = (details && details.quote) || null;
    var full = (quote && quote.customer && quote.customer.name) || (dq && dq.customer) || '';
    var first = T.firstName(full);
    if (first) return first;
    var to = (Array.isArray(ctx && ctx.toEmails) ? ctx.toEmails : [])[0] || '';
    return T.firstName(String(to).split('@')[0].replace(/[._-]+/g, ' '));
  }

  /* ── "I called and left you a voicemail last week" ─────────────────────────
     THE block that makes a follow-up feel written rather than generated. Ryan: "these
     emails now need to clue into my phone calls also ... just personalized based on the
     phone call and day/time of the call."

     Takes a contactSince() reading and describes THE MOST RECENT ATTEMPT, because that is
     the one the customer might remember. `tail` is the template's own reason-for-calling
     clause (" to follow up on the quote", " to see about your timeline") appended inside
     the sentence, so each email can say why it called without restating how it called.

     Returns null when nothing has been tried since - Ryan: "sometimes I'll call today and
     won't send an email, but then next week I'll send the email 1 but won't make another
     call", so an email with no call behind it must simply not mention one.

     ⛔ AND IT RETURNS NULL WHEN THE ATTEMPT HAS GONE STALE (Ryan 2026-09-15). Follow-up 1 was
     opening with "I called and left you a voicemail on the 26th of August to follow up on the
     quote I sent over" - true, and it read as though nobody had looked at the deal in three
     weeks. Ryan: *"No more than 2 days ago. Meaning, 3 days ago would be too much. That would
     make people stop and wonder 'why did you call me 3 days ago and only today, 3 days later,
     decide to email me and mention it?'"* So past two days the sentence is simply left out and
     the email opens on its ask, which is what it should have been leading with anyway.

     ⛔ TWO FLAGS, BECAUSE THERE ARE TWO SENTENCES HERE AND THEY FAIL DIFFERENTLY.

       an ATTEMPT (voicemail / tried / text) → `fresh`, the 2-day cut. It is justifying a
         chase, so the date is load-bearing and a stale one begs "why now?".
       CONNECTED ("thanks again for your time on the phone") → `recent`, Ryan's own
         2026-08-26 cut at "last week". It is a pleasantry, not a justification: thanking
         somebody for a call last week reads fine, and holding it to two days would delete a
         warm opener for no reason. Beyond `recent` it names a calendar date and becomes the
         filing cabinet Ryan disqualified by name - which this file was NOT checking until
         now, so "Thanks again for your time on the phone on the 26th of August" could ship.

     ⚠️ BOTH ARE READ OFF THE LADDER, never re-derived here. See _whenPhrase in the kernel. */
  /* CUSTOM_DELAYEDSTART_BY_CLAUDE (Ryan 2026-09-28) — `opts.nameDay`: the email was SCHEDULED to
     go days after the call, on purpose. Ryan: *"the email should instead start with the line: 'I
     called you on [day of the week] to follow up on the quote I sent over.'"* The two-day cut above
     exists to stop a stale call reading as an afterthought; a deliberate gap is the opposite case -
     the call was its own touch and this email is the second one - so the call is named by its
     WEEKDAY ("on Monday") and the freshness cut is not applied. Still dropped past six days, where a
     weekday name becomes ambiguous (which Monday?). */
  function _weekdayName(ts) {
    try {
      return new Intl.DateTimeFormat('en-US', { timeZone: 'America/Denver', weekday: 'long' })
        .format(new Date(ts));
    } catch (_) { return ''; }
  }

  function callReference(contact, tail, nowMs, opts) {
    var last = contact && contact.last;
    if (!last) return null;
    var now = nowMs || T.now();
    var w = T.whenPhrase(last.ts, now);
    if (opts && opts.nameDay) {
      var _age = (now - new Date(last.ts).getTime()) / 86400000;
      var _day = _weekdayName(last.ts);
      if (!(_age >= 0 && _age < 6.5) || !_day) return null;
      w = { phrase: 'on ' + _day, just: false, recent: true, fresh: true };
    }
    var vals = { WHEN: w.phrase ? ' ' + w.phrase : '', TAIL: tail || '' };
    var types = Array.isArray(last.types) && last.types.length ? last.types : (last.type ? [last.type] : []);

    /* ⛔ WHAT HAPPENED, decided here; what to SAY about it, in COPY at the top of the file.
       "Connected" is last because it is also the fallback for an entry logged before the
       outcome selector existed — thanking someone for their time is the only safe thing to say
       about a call we know they answered. */
    var kind = (types.indexOf('text') !== -1) ? 'text'
             : (last.callOutcome !== 'no-answer') ? 'connected'
             : (last.callVm === 'left') ? 'voicemail'
             : 'tried';

    if (kind === 'connected') return w.recent ? COPY['call.connected']({ WHEN: vals.WHEN }) : null;
    if (!w.fresh) return null;
    /* ⚠️ "just now" is the one rung of the time ladder that moves the word in FRONT of the verb,
       which is why it is a separate sentence rather than a different value for {WHEN}. */
    return COPY['call.' + kind + '.' + (w.just ? 'just' : 'dated')](vals);
  }

  /* ── "I've left you a couple of voicemails and sent you a few emails" ──────
     The tally, for an email that is explicitly counting attempts. Ryan's own example, and
     his own caveat: "just depends on what's recorded in the DD."

     ⛔ VERBS ARE GROUPED BY WHAT THEY DO TO THE THING. You LEAVE a voicemail, you SEND a
     text, you EMAIL someone. Folding them under one verb ("sent you a couple of voicemails")
     is the kind of small wrongness a reader notices without being able to say why.

     Returns null when there is nothing to count, so the final email does not claim a
     campaign that did not happen. */
  /* These clauses carry no links, so flattening runs back to a string loses nothing — and
     joinWords needs strings to put the commas and the "and" between them. */
  function _flat(runs) {
    return (runs || []).map(function (r) { return r.t; }).join('');
  }

  function contactTally(contact) {
    if (!contact) return null;
    var parts = [];
    var vm = (contact.vms || []).length;
    var tx = (contact.texts || []).length;
    var em = contact.emailsOut || 0;
    /* ⛔ THE COUNTING IS LOGIC, THE CLAUSE IS WORDING. `quantity` turns 2 into "a couple of
       voicemails" and stays in code; what that clause reads like is in COPY. */
    if (vm) parts.push(_flat(COPY['tallyVoicemails']({ COUNT: quantity(vm, 'voicemail') })));
    if (tx) parts.push(_flat(COPY['tallyTexts']({ COUNT: quantity(tx, 'text') })));
    if (em === 1) parts.push(_flat(COPY['tallyEmails.once']({})));
    else if (em === 2) parts.push(_flat(COPY['tallyEmails.couple']({})));
    else if (em >= 3) parts.push(_flat(COPY['tallyEmails.few']({})));
    if (!parts.length) return null;
    return COPY['tallyLead']({ PARTS: joinWords(parts) });
  }

  /* ── naming the products ──────────────────────────────────────────────────
     Two shapes, and the caller picks by how many there are. Ryan's rule: up to three run
     inline and hyperlinked; four or more become a bulleted list under a category lead-in,
     because a sentence naming five products is not a sentence any more.

     inlineProducts() returns RUNS to splice into a sentence.
     bulletProducts() returns whole PARAGRAPH blocks, one per item. */
  function inlineProducts(items) {
    if (!items || !items.length) return null;
    return T.joinRuns(items);
  }

  function bulletProducts(items) {
    return (items || []).map(function (it) {
      return { list: 'bullet', runs: [it.url ? { t: it.title, link: it.url } : { t: it.title }] };
    });
  }

  /* The noun for a set too long to list: "flooring products" / "gym equipment". Used both
     as a bullet lead-in and, on its own, as the object of a question ("still in the market
     for the gym equipment we quoted?"). */
  function productNoun(items) {
    return T.categoryPhrase(items);
  }

  /* ── "the quote" vs "the quotes" ──────────────────────────────────────────
     Counted from the attachments actually on THIS email when there are any, falling back
     to the deal. One definition, because three templates ask the same question and a
     mismatch between two of them in the same thread reads as carelessness. */
  function quoteWords(ctx, ref) {
    var attached = T.attachedQuoteRefs((ctx && ctx.attachments) || [], ref);
    var n = attached.length || 1;
    return {
      count: n,
      quote: n > 1 ? 'quotes' : 'quote',
      pdf: n > 1 ? 'PDFs' : 'PDF',
      it: n > 1 ? 'them' : 'it',
      itemThey: n > 1 ? 'they' : 'it',
    };
  }

  /* ── the subject line ─────────────────────────────────────────────────────
     Ryan's shape, verbatim: "[First Last] [flooring|paver|equipment] quote - Blue Sky
     Fitness Supply".

     ⛔ THIS SUBJECT IS THE WHOLE THREAD'S NAME. The follow-up ladder replies INTO this
     message, so whatever goes here is what the customer sees on all five emails. That is
     the argument for naming the customer and the category in it: months later they can find
     the thread by searching either.

     THE CATEGORY, in Ryan's precedence:
       flooring  - an ECORE rolled rubber / tile / plank product that is NOT an accessory
       paver     - what they asked about is a paver
       equipment - anything else on the site
     ⚠️ Accessories are excluded BY NAME (weld rod, adhesive, cleaner, reducer, sealer...)
     because a quote for rolls plus adhesive is still a flooring quote, but a quote for a
     bucket of adhesive on its own is not - and if accessories counted, the second would be
     mislabelled every time. Unknown words fall to "equipment", which is the safe floor:
     it is true of everything we sell. */
  var _ACCESSORY_RX = /\b(weld\s*rod|rod|adhesive|glue|cleaner|sealer|e-?finish|finish|e-?grip|grip|primer|trowel|reducer|transition|e-?strip|strip|edging|tape|spray|bucket|gallon|applicator|roller)\b/i;
  var _FLOORING_RX = /\b(roll|rolls|rolled|tile|tiles|plank|planks)\b/i;

  /* ⚠️ THE `unit:` TAG OUTRANKS THE TITLE (see T.isPaver in the kernel): merchandising sets
     unit:sqft / unit:tile / unit:paver deliberately and it survives a product rename, so the
     title regex is only the fallback for anything not tagged.
     Delegates to the kernel — _categoryPhrase needs the same test and lives a layer down,
     so the definition moved there rather than being written twice. (Ryan 2026-08-19.) */
  function _isPaver(it) { return T.isPaver(it); }
  function _isFlooringUnit(it) {
    if (it && it.uom) return it.uom === 'sqft' || it.uom === 'tile';
    return _FLOORING_RX.test(String((it && it.title) || ''));
  }

  function subjectCategory(items) {
    var list = (items || []);
    /* CUSTOM_INQUIRYFOLLOWUP_BY_CLAUDE (Ryan 2026-09-26) — A WEIGHT DESIGNER INQUIRY NAMES ITS WEIGHT.
       It read "Shelby Gregory equipment quote": the WD lines carry no vendor or unit, so they fell to
       the catch-all. Every line from the designer carries its product `wdLabel` ("Bumper Plates"),
       so one kind reads "custom bumper plate", and a mix of kinds "custom weight". */
    if (list.length && list.every(function (it) { return it && it.wd; })) {
      var kinds = {};
      list.forEach(function (it) {
        var w = String(it.wdLabel || '').trim().toLowerCase().replace(/s$/, '');
        if (w) kinds[w] = 1;
      });
      var ks = Object.keys(kinds);
      return 'custom ' + (ks.length === 1 ? ks[0] : 'weight');
    }
    var hasFlooring = list.some(function (it) {
      if (!T.isEcore(it.vendor)) return false;
      if (_isPaver(it)) return false;
      /* Accessories are excluded by NAME even when they carry a unit tag: a weld rod sold
         by the tile is still not what makes a quote a flooring quote. */
      if (_ACCESSORY_RX.test(String(it.title || ''))) return false;
      return _isFlooringUnit(it);
    });
    if (hasFlooring) return 'flooring';
    if (list.some(_isPaver)) return 'paver';
    return 'equipment';
  }

  /* The full name, because the subject wants "Mike Halloran" and the greeting wants "Mike".
     Same source order as customerFirstName; an email address is never a name. */
  function customerFullName(details, dq, ctx) {
    var quote = (details && details.quote) || null;
    var full = String((quote && quote.customer && quote.customer.name) || (dq && dq.customer) || '').trim();
    if (full && full.indexOf('@') === -1) {
      /* "SARAH VANCE" and "sarah vance" both become "Sarah Vance"; already-mixed case is
         left alone so "McDonald" and "DeShawn" survive, same rule as firstName. */
      return full.split(/\s+/).map(function (w) {
        if (!w) return w;
        return (w === w.toUpperCase() || w === w.toLowerCase())
          ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w;
      }).join(' ');
    }
    return '';
  }

  /* CUSTOM_INQUIRYSUBJECT_BY_CLAUDE (AIOPS 0d681a4b5eb7, Ryan 2026-10-11) — the new-inquiry subject.
     Ryan: "[First Last] - Blue Sky Fitness Supply [product type] inquiry - [Reference Number]", and
     "sample request" in place of "inquiry" when the lead asked for samples. It was "Gabrielle Correia
     equipment quote - Blue Sky Fitness Supply" on RW-1494-A: no ref, and "equipment" for a customer
     who asked about flooring. The product type is left out when nothing tells us (no products found):
     "Blue Sky Fitness Supply inquiry" (Ryan: just "inquiry"). The ref is the FULL ref (RW-1494-A); on a
     new email the composer moves it to follow the quote PDFs attached. */
  function quoteSubject(fullName, items, opts) {
    var o = opts || {};
    var cat = (items && items.length) ? subjectCategory(items) : '';
    var kind = o.sample ? 'sample request' : 'inquiry';
    var ref = String(o.ref || '').trim();
    return (fullName ? fullName + ' - ' : '') + 'Blue Sky Fitness Supply ' + (cat ? cat + ' ' : '') + kind + (ref ? ' - ' + ref : '');
  }

  /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-09-06) — the SEND-QUOTE subject, the shape the
     Workstation's Send Quote button has always seeded ("Quote for {Name} - Blue Sky Fitness
     Supply - {ref}", workstation-save.js openNew). The first-quote template returns it so that
     picking the template from the Deal Dashboard's ✉ pill — which opens on an EMPTY subject —
     fills the box the way Send Quote would. Ryan: *"I need the subject line to inject itself in
     the same way it would if we were sending the first quote to the customer via the 'Send Quote'
     button in the QW."* ⚠️ Deliberately NOT `quoteSubject` above: that is the new-inquiry shape.
     Two shapes exist because two emails exist; this one carries the ref because the Send Quote
     one always has, and the ref is what threads a reply back to the deal. */
  function sendQuoteSubject(fullName, ref, opts) {
    var who = String(fullName || '').trim();
    var r = String(ref || '').trim();
    /* CUSTOM_UPDATEDQUOTE_BY_CLAUDE (Ryan 2026-09-24) — the 2nd+ quote email leads "Updated
       quote for …"; same shape otherwise, so a reply still threads back to the deal by ref. */
    var lead = (opts && opts.updated) ? 'Updated quote' : 'Quote';
    return (who ? lead + ' for ' + who + ' - ' : lead + ' - ') + 'Blue Sky Fitness Supply' + (r ? ' - ' + r : '');
  }

  /* ============================================
     💳 THE PAYMENT SENTENCE (Ryan 2026-10-27), shared by the "Payment link" snippet and both quote emails
     ============================================
     *"To finalize your order, please sign the proposal and [submit payment | submit your down payment | (Net 30: nothing)]
     by clicking here. We have a few different payment methods you can choose from including ACH, wire, check, Zelle, and
     card."* The 1st Quote email opens it "When the appropriate time comes, please ..." instead (Ryan 2026-10-27: a first
     quote is not an invoice, the softener he asked for on 2026-08-25 stays); the snippet and the 2nd+ Quote keep
     "To finalize your order".
     The link and the terms come from GET /deal-pay-link: an offline deal's sign page, where they sign and then pay by any
     of those methods, so no quote has to be attached. When ONE quote is attached its ref is used (the -B on the email,
     not the deal's -A); with several, the deal's own ref, and a warning.
     ⚠️ A website quote has no sign page: its link IS the checkout, so the sentence drops "sign the proposal" and the
     methods line (that checkout takes cards and Shop Pay, not a check).
     ⛔ ONE SENTENCE, ONE DEFINITION. This replaced the old "The ordering instructions are written on the second page of the
     quote. ... the quickest payment method is to use a card by clicking here." in all three places at once; a second
     copy is how they drift. */

  /* Matches the attach menu's chain rows, which is where a rep last saw these numbers.
     Whole dollars: cents on a $12,483.19 proposal are noise in a sentence. */
  function money(n) {
    var v = parseFloat(n);
    if (!isFinite(v) || v <= 0) return '';
    try {
      return v.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
    } catch (_) { return '$' + Math.round(v).toLocaleString(); }
  }

  /* Rejects with an Error whose message says why (fees still pending, not your deal, closed): the snippet shows it, a
     template turns it into a warning and leaves the sentence out. */
  function payLinkFor(ctx) {
    var api = T.api();
    if (!api) return Promise.reject(new Error('the portal is still loading. Try again in a second.'));
    var dealRef = String((ctx && ctx.ref) || '').trim();
    var attached = (T.attachedQuoteRefs ? T.attachedQuoteRefs((ctx && ctx.attachments) || [], dealRef) : []) || [];
    var ref = attached.length === 1 ? attached[0] : dealRef;
    var warnings = [];
    if (attached.length > 1) warnings.push(attached.length + ' proposals are attached, so the link is for ' + ref + '. Check it is the one you mean.');
    if (!ref) return Promise.reject(new Error('this email is not on a deal, so there is no payment link to use.'));
    return api.get('/deal-pay-link?ref=' + encodeURIComponent(ref)).then(function (d) {
      if (!d || !d.link) throw new Error((d && (d.detail || d.error)) || 'no payment link for ' + ref + '.');
      return { link: d.link, kind: d.kind, termsKind: d.termsKind || 'unknown', ref: d.ref || ref, warnings: warnings };
    }, function (e) {
      /* the API helper rejects on a non-2xx: its body names why (fees still pending, not your deal, closed) */
      var b = e && (e.body || e.data);
      throw new Error((b && (b.detail || b.error)) || (e && e.message) || 'the payment link could not be read.');
    });
  }

  /* `p` is what payLinkFor resolved. opts.lead 'appropriate' = the 1st Quote opening. */
  function paymentSentence(p, opts) {
    var sign = p.kind === 'sign';
    var k = p.termsKind;
    var half = k === '50-50' || k === '50-50-net-30';
    var warnings = (p.warnings || []).slice();
    if (k === 'unknown') warnings.push('No payment terms are set on ' + p.ref + ', so it asks for payment (the Pay in Full wording).');
    var open = (opts && opts.lead === 'appropriate') ? 'When the appropriate time comes, please ' : 'To finalize your order, please ';
    var lead = sign
      ? open + 'sign the proposal' + (k === 'net-30' ? '' : half ? ' and submit your down payment' : ' and submit payment')
      : open + (half ? 'submit your down payment' : 'submit payment');
    var runs = [{ t: lead + ' by ' }, { t: 'clicking here', link: p.link }, { t: '.' }];
    if (sign && k !== 'net-30') runs.push({ t: ' We have a few different payment methods you can choose from including ACH, wire, check, Zelle, and card.' });
    return { runs: runs, warnings: warnings };
  }


  /* ============================================
     THE WEIGHTS THEY DESIGNED (Ryan 2026-09-02, reworded 2026-11-02)
     ============================================
     The sentence that goes on a first quote when the deal started in the Weight Designer.
     CUSTOM_WDAUTOQUOTE_BY_CLAUDE (Ryan 2026-11-02) - IN HIS WORDS, THE INQUIRY FOLLOW-UP'S OWN LINE.
     A Weight Designer quote is now built the moment the lead lands, so the 1st Quote is usually the
     FIRST email the buyer gets and the follow-up that used to carry this line is skipped. Ryan:
     *"we need to include the 'As a reminder, here is a link to the weights you designed.
     Additionally click here to see a design we made for you...' line in the 1st quote to customer
     template"*. Same sentence and the same two links as email-tpl-inquiry.js `designs.both`:

       theirs only    -> "As a reminder, [here is a link] to the weights you designed."
       theirs + ours  -> that, then "Additionally [click here] to see a design we made for you, ..."
       ours only      -> "[Click here] to see a design we made for you, ..."

     Always its OWN paragraph (Ryan put the line break in himself on 2026-09-02).
     ⚠️ TWO LINKS, NOT THE CHAIN: theirs opens their design, ours opens OUR newest version on its own
     solo link (`T.wdDesigns` in email-tpl-snippets.js picks it, the same lookup the inquiry uses).

     ⛔ IT OMITS ITSELF RATHER THAN REFUSING. A template owns a whole email: blocking a quote over a
     missing design link would be the wrong trade, so with no link this returns null.
     ⛔ NO EM DASHES. Customer-facing.

     @param {{theirsUrl?: string, oursUrl?: string}} o
     @returns {{runs, standalone, warnings}|null} */
  function weightDesignLine(o) {
    var opts = o || {};
    var theirs = String(opts.theirsUrl || '');
    var ours = String(opts.oursUrl || '');
    if (!theirs && !ours) return null;
    var made = ' to see a design we made for you, where we took your original design and made some modifications to it which we think enhanced the overall appearance.';
    var runs;
    if (theirs && ours) {
      runs = [{ t: 'As a reminder, ' }, { t: 'here is a link', link: theirs }, { t: ' to the weights you designed. Additionally ' },
        { t: 'click here', link: ours }, { t: made }];
    } else if (theirs) {
      runs = [{ t: 'As a reminder, ' }, { t: 'here is a link', link: theirs }, { t: ' to the weights you designed.' }];
    } else {
      runs = [{ t: 'Click here', link: ours }, { t: made }];
    }
    return { standalone: true, runs: runs, warnings: [] };
  }

  window.RQDTplBlocks = {
    subjectCategory: subjectCategory,
    payLinkFor: payLinkFor,
    paymentSentence: paymentSentence,
    weightDesignLine: weightDesignLine,
    money: money,
    customerFullName: customerFullName,
    quoteSubject: quoteSubject,
    sendQuoteSubject: sendQuoteSubject,
    quantity: quantity,
    joinWords: joinWords,
    greeting: greeting,
    signOff: signOff,
    customerFirstName: customerFirstName,
    callReference: callReference,
    contactTally: contactTally,
    inlineProducts: inlineProducts,
    bulletProducts: bulletProducts,
    productNoun: productNoun,
    quoteWords: quoteWords,
  };
})();
